/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import {
    Segment,
    Header,
    Icon,
    Label,
    Table,
    Button,
    Grid,
    Placeholder,
    Image,
} from 'semantic-ui-react'
import { useParams, Link } from 'react-router-dom-v5-compat'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getDispute } from '../../services/Disputes/disputes.service'
import { getOrder, getOrderPickingProofsUrls } from '../../services/Order/Order.service'
import useUser from '../Shared/UserProvider/useUser'
import useBreadcrumb from '../NavBar/useBreadcrumb'
import formatDate from '../../helpers/formatDate'
import { useEffect } from 'react'

const statusColor = {
    OPEN: 'grey',
    CONTESTED: 'orange',
    RECOVERED: 'green',
    PARTIALLY_RECOVERED: 'green',
    REFUNDED: 'red',
}

const itemStatusColor = {
    REFUNDED: 'red',
    RECOVERED: 'green',
    LOST: 'grey',
    CONTESTED: 'orange',
}

const DisputeDetail = () => {
    const [t] = useTranslation()
    const { orderId } = useParams()
    const { user } = useUser()
    const { setPaths } = useBreadcrumb()

    useEffect(() => {
        setPaths([
            { text: t('breadcrumb.home'), link: true, path: '/' },
            { text: t('dispute.title'), link: true, path: '/orders/dispute' },
            { text: orderId, link: false },
        ])
    }, [orderId])

    const { data: response, isLoading: isDisputeLoading } = useQuery({
        queryKey: ['dispute', orderId],
        queryFn: () => getDispute(orderId),
        enabled: !!orderId,
    })

    const { data: order } = useQuery({
        queryKey: ['order', orderId],
        queryFn: () => getOrder(orderId),
        enabled: !!orderId,
    })

    const { data: pickingProofs } = useQuery({
        queryKey: ['order', orderId, 'picking-proofs'],
        queryFn: () => getOrderPickingProofsUrls(orderId),
        enabled: !!orderId,
    })

    const dispute = response
    const isLoading = isDisputeLoading

    const formatMoney = (cents) => {
        if (!cents && cents !== 0) return '—'
        return (cents / 100).toLocaleString(user.getCompanyLocale(), {
            style: 'currency',
            currency: user.getCompanyCurrency(),
        })
    }

    if (isLoading) {
        return (
            <Segment>
                <Placeholder>
                    <Placeholder.Header image>
                        <Placeholder.Line />
                        <Placeholder.Line />
                    </Placeholder.Header>
                    <Placeholder.Paragraph>
                        <Placeholder.Line />
                        <Placeholder.Line />
                        <Placeholder.Line />
                    </Placeholder.Paragraph>
                </Placeholder>
            </Segment>
        )
    }

    if (!dispute) {
        return (
            <Segment placeholder>
                <Header icon>
                    <Icon name="search" />
                    {t('dispute.not_found')}
                </Header>
                <Button as={Link} to="/orders/dispute" primary>
                    {t('dispute.back_to_list')}
                </Button>
            </Segment>
        )
    }

    return (
        <div css={css`padding: 1rem;`}>
            <Header as="h2">
                {t('dispute.detail_title')} — {orderId}
                <Label
                    color={statusColor[dispute.status] || 'grey'}
                    css={css`margin-left: 1rem !important;`}
                >
                    {dispute.status ? t(`dispute.status_values.${dispute.status}`) : '—'}
                </Label>
            </Header>

            <Segment>
                <Button as={Link} to={`/orders/${orderId}`} primary>
                    <Icon name="eye" />
                    {t('dispute.view_order')}
                </Button>
            </Segment>

            <Grid stackable>
                <Grid.Row columns={2}>
                    <Grid.Column>
                        <Segment>
                            <Header as="h4">{t('dispute.summary')}</Header>
                            <Table basic="very" celled>
                                <Table.Body>
                                    <Table.Row>
                                        <Table.Cell><strong>{t('dispute.platform')}</strong></Table.Cell>
                                        <Table.Cell>
                                                                                        {/* <Image size="tiny" src={order.platform.logo}/> */}
                                            
                                            {dispute.posBrandPlatform?.platform?.name || '—'}
                                            {' — '}
                                            {dispute.posBrandPlatform?.brand?.name || '—'}
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell><strong>{t('dispute.date')}</strong></Table.Cell>
                                        <Table.Cell>
                                            {dispute.disputeCreationDate
                                                ? formatDate(dispute.disputeCreationDate)
                                                : '—'}
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell><strong>{t('dispute.reason')}</strong></Table.Cell>
                                        <Table.Cell>{dispute.disputeReason || '—'}</Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell><strong>{t('dispute.order_amount')}</strong></Table.Cell>
                                        <Table.Cell>{formatMoney(dispute.orderAmount)}</Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell><strong>{t('dispute.dispute_amount')}</strong></Table.Cell>
                                        <Table.Cell>{formatMoney(dispute.disputeAmount)}</Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell><strong>{t('dispute.recovered_amount')}</strong></Table.Cell>
                                        <Table.Cell>{formatMoney(dispute.recoveredAmount)}</Table.Cell>
                                    </Table.Row>
                                </Table.Body>
                            </Table>
                        </Segment>
                    </Grid.Column>

                    {dispute.customerDetails && (
                        <Grid.Column>
                            <Segment>
                                <Header as="h4">{t('dispute.customer_context')}</Header>
                                <Table basic="very" celled>
                                    <Table.Body>
                                        {dispute.customerDetails.inputDescription && (
                                            <Table.Row>
                                                <Table.Cell><strong>{t('dispute.customer_feedback')}</strong></Table.Cell>
                                                <Table.Cell>{dispute.customerDetails.inputDescription}</Table.Cell>
                                            </Table.Row>
                                        )}
                                        {dispute.customerDetails.branchName && (
                                            <Table.Row>
                                                <Table.Cell><strong>{t('dispute.branch_name')}</strong></Table.Cell>
                                                <Table.Cell>{dispute.customerDetails.branchName}</Table.Cell>
                                            </Table.Row>
                                        )}
                                    </Table.Body>
                                </Table>
                            </Segment>
                        </Grid.Column>
                    )}
                </Grid.Row>
            </Grid>

            {pickingProofs && pickingProofs.length > 0 && (
                <Segment>
                    <Header as="h4">{t('dispute.picking_proofs')}</Header>
                    <div css={css`
                        display: flex;
                        flex-wrap: wrap;
                        gap: 1rem;
                        margin-top: 1rem;
                    `}>
                        {pickingProofs.map((proofUrl, index) => (
                            <div key={proofUrl} css={css`
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                            `}>
                                <Image
                                    src={proofUrl}
                                    size="small"
                                    css={css`
                                        border: 2px solid #21ba45;
                                        max-width: 200px;
                                        object-fit: contain;
                                    `}
                                    alt={t('dispute.picking_proof_alt', { index: index + 1 })}
                                />
                                <Button
                                    as="a"
                                    href={proofUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="tiny"
                                    color="teal"
                                    css={css`margin-top: 0.5rem;`}
                                >
                                    <Icon name="download" />
                                    {t('dispute.download_proof')}
                                </Button>
                            </div>
                        ))}
                    </div>
                </Segment>
            )}

            {dispute.disputeItems?.length > 0 && (
                <Segment>
                    <Header as="h4">{t('dispute.items')}</Header>
                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>{t('dispute.item.name')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('dispute.item.category')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('dispute.item.refund_reason')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('dispute.item.status')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('dispute.item.recovering_info')}</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {dispute.disputeItems.map((item) => {
                                const estimationPercentage = item.recoveringChangeEstimation != null
                                    ? (item.recoveringChangeEstimation * 100).toFixed(2) + '%'
                                    : null
                                
                                const hasPickingProof = item.recoveringProofElements && (
                                    item.recoveringProofElements.pickingMethod === 'SCAN' ||
                                    item.recoveringProofElements.pickingProofs === 'MAYBE'
                                )
                                
                                const recoveryInfo = []
                                if (estimationPercentage) {
                                    recoveryInfo.push(estimationPercentage)
                                }
                                if (hasPickingProof) {
                                    recoveryInfo.push(t('dispute.item.has_picking_proof'))
                                }
                                const displayInfo = recoveryInfo.length > 0 ? recoveryInfo.join(', ') : '—'

                                return (
                                    <Table.Row key={item.id}>
                                        <Table.Cell>{item.name || '—'}</Table.Cell>
                                        <Table.Cell>{item.itemCategory || '—'}</Table.Cell>
                                        <Table.Cell>{item.refundReason || '—'}</Table.Cell>
                                        <Table.Cell>
                                            <Label color={itemStatusColor[item.refundStatus] || 'grey'}>
                                                {item.refundStatus ? t(`dispute.item.status_values.${item.refundStatus}`) : '—'}
                                            </Label>
                                        </Table.Cell>
                                        <Table.Cell>{displayInfo}</Table.Cell>
                                    </Table.Row>
                                )
                            })}
                        </Table.Body>
                    </Table>
                </Segment>
            )}

            <Button as={Link} to="/orders/dispute">
                <Icon name="left chevron" />
                {t('dispute.back_to_list')}
            </Button>
        </div>
    )
}

export default DisputeDetail
